import { useTranslation } from "react-i18next";
import { Group } from "../../../../../backoffice/dtos/groups/group";
import { Button, Col, Input, InputGroup, InputGroupText, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import { useGetProductGroupsQuery } from "../../../../../backoffice/apis/productAPI";
import { SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faSearch, faTimes, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useGetDepartmentProductGroupsQuery, usePostDepartmentProductGroupsMutation } from "../../../../../backoffice/apis/groupsAPI";
import DepartmentProductGroup from "../../../../../backoffice/dtos/groups/DepartmentProductGroup";

export default function DepartmentsProductGroupsSettings(
    {
        group,
        toggle
    }: {
        group: Group,
        toggle: () => void
    }
) {
    const { t } = useTranslation(['product_groups','common'])

    const {
        data: groupProductGroups,
        isFetching: groupProductGroupsLoading,
        error: groupProductGroupsError
    } = useGetDepartmentProductGroupsQuery(group, { refetchOnMountOrArgChange: true })

    const {
        data: productGroups,
        isFetching: productGroupsLoading,
        error: productGroupsError
    } = useGetProductGroupsQuery(undefined, { refetchOnMountOrArgChange: true })

    let modalBody: JSX.Element
    if (productGroupsLoading || groupProductGroupsLoading) {
        modalBody = (
            <>
                <ModalBody className="d-flex flex-row justify-content-center">
                    <Spinner className="m-2" />
                </ModalBody>
            </>
        );
    } else if (productGroupsError || !productGroups || groupProductGroupsError || !groupProductGroups) {
        modalBody = (
            <>
                <ModalBody>
                    Failed to fetch data
                </ModalBody>
            </>
        );
    } else {
        modalBody = (
            <InnerDepartmentsProductGroupsSettings
                currentProductGroups={groupProductGroups}
                group={group}
                toggle={toggle}
                productGroups={productGroups}
            />
        )
    }

    return (
        <>
            <Modal isOpen={true} toggle={toggle} size="xl">
                <ModalHeader toggle={toggle}>
                    {t("Department's Assigned Product Groups")}
                </ModalHeader>
                {modalBody}
            </Modal>
        </>
    );
}


function InnerDepartmentsProductGroupsSettings(
    {
        group,
        currentProductGroups,
        toggle,
        productGroups
    }: {
        group: Group;
        currentProductGroups: DepartmentProductGroup[];
        toggle: () => void;
        productGroups: string[];
    }
) {
    const { t } = useTranslation(['department_info','common'])
    const [groupProductGroups, setGroupProductGroups] = useState<ProductGroupItem[]>([])
    const [filter, setFilter] = useState('')
    const [activeFilter, setActiveFilter] = useState('')
    const [loading, setLoading] = useState(false)

    const [ postDepartmentProductGroup ] = usePostDepartmentProductGroupsMutation();

    const allItems = useMemo(() => {
        return productGroups.map(m => new ProductGroupItem(m, false, setGroupProductGroups));
    }, [productGroups])

    useEffect(() => {
        const currentItems = currentProductGroups.map(m => m.productGroupCode)
        const activeItems = allItems.filter(m => currentItems.includes(m.name));

        for(const item of activeItems) {
            item.active = true
        }

        setGroupProductGroups(activeItems);
    }, [currentProductGroups, allItems])

    const handleUpdate = useCallback(async () => {
        try {
            setLoading(true)

            const items = groupProductGroups.map(it => it.name);
            await postDepartmentProductGroup({ groupId: group.id, productGroupCodes: items }).unwrap()
            // await updateDepartmentCustomerOrder({ groupId: group.id, customers: customers }).unwrap()
            toggle()
        } catch(error) {
            toast("Could not update order.")
        } finally {
            setLoading(false)
        }
    }, [groupProductGroups, group]);

    const askToUpdate = useCallback(() => {
        confirmAlert({
          title: t("Update department's product groups"),
          message: t("Are you sure you want to update the department's product groups?"),
          buttons: [{ label: t('yes',{ns:'common'}), onClick:() => handleUpdate() }, { label: t('no',{ns:'common'}) }]
        })
    }, [handleUpdate]);

    const unusedCustomers = useMemo(() => {
        const currentCustomers = new Set(groupProductGroups)
        return allItems.filter(c => !currentCustomers.has(c) && c.name.includes(filter));
    }, [groupProductGroups, filter, allItems]);

    return (
        <>
            <ModalBody>
                <Row>
                    <Col md='6'>
                        <InputGroup className="mb-3">
                            <Input className="form-control" placeholder= {t('search',{ns:'common'})} value={filter} onChange={(it: any) => setFilter(it.target.value)}/>
                            { filter.length > 0
                                ? <Button color="danger" onClick={() => setFilter('')}><FontAwesomeIcon icon={faTimes}/></Button>
                                : <InputGroupText><FontAwesomeIcon icon={faSearch}/></InputGroupText>
                            }
                        </InputGroup>
                    </Col>
                    <Col md='6'>
                        <InputGroup className="mb-3">
                            <Input className="form-control" placeholder= {t('search',{ns:'common'})} value={activeFilter} onChange={(it: any) => setActiveFilter(it.target.value)}/>
                            { filter.length > 0
                                ? <Button color="danger" onClick={() => setFilter('')}><FontAwesomeIcon icon={faTimes}/></Button>
                                : <InputGroupText><FontAwesomeIcon icon={faSearch}/></InputGroupText>
                            }
                        </InputGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md='6'>
                        <div className="d-flex flex-column flex-fill overflow-auto" style={{height: 500}}>
                            { unusedCustomers.map(
                                it => (
                                    <UnscrollableCustomerItem productGroupItem={it}/>
                                )
                            )
                            }
                        </div>
                    </Col>
                    <Col md='6'>
                        <div className="d-flex flex-column flex-fill overflow-auto" style={{height: 500}}>
                            {
                                groupProductGroups.filter(m => m.name.includes(activeFilter)).map(
                                    it => (
                                        <UnscrollableCustomerItem productGroupItem={it}/>
                                    )
                                )
                            }
                        </div>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="success" onClick={askToUpdate} disabled={loading}>
                     {loading && <Spinner size="sm"/> } {t('update',{ns:'common'})}
                </Button>{' '}
                <Button color="danger" onClick={toggle} disabled={loading}>
                    {loading && <Spinner size="sm"/> } {t('cancel',{ns:'common'})}
                </Button>
            </ModalFooter>
        </>
    );

}

function UnscrollableCustomerItem(
    props: {
        productGroupItem: ProductGroupItem
    } 
) {
    const item = useMemo(() => props.productGroupItem, [props.productGroupItem]);
    return (
        <Row className="mb-2">
            <Col className="d-flex flex-row align-items-center">
                <h5>{item.name}</h5>
            </Col>
            {
                item.active ? (
                    <Col lg='auto' className="d-flex justify-content-end">
                        <Button color="danger" onClick={item.remove}><FontAwesomeIcon icon={faXmark} /></Button>
                    </Col>
                ) : (
                    <Col lg='auto' className="d-flex justify-content-end">
                        <Button color="success" onClick={item.add}><FontAwesomeIcon icon={faArrowRight} /></Button>
                    </Col>
                )
            }
        </Row>
    )
}

class ProductGroupItem {
    name: string;
    active: boolean;
    setGroupProductGroup: React.Dispatch<SetStateAction<ProductGroupItem[]>>
    constructor(name: string, active: boolean, setGroupProductGroup: React.Dispatch<SetStateAction<ProductGroupItem[]>>) {
        this.name = name;
        this.active = active;
        this.setGroupProductGroup = setGroupProductGroup;
    }

    remove: () => void = () => {
        this.active = false;
        this.setGroupProductGroup(ref => {
            return ref.filter(m => m.name !== this.name);
        });
    }

    add: () => void = () => {
        this.active = true;
        this.setGroupProductGroup(ref => {
            if (ref.includes(this)) {
                return ref;
            }

            return [...ref, this]
        });
    }
}